import React from 'react';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import {
  Accordion, AccordionSummary, AccordionDetails, Box, Stack, Typography, Chip, Button, Card, CardContent,
  Grid, Alert, LinearProgress, Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StorefrontIcon from '@mui/icons-material/Storefront';
import {
  VENDOR_ACCOUNTS_QUERY, VENDOR_OFFERINGS_QUERY, VENDOR_OFFERING_FIELD_DEFS_QUERY, ALL_SERVICE_CATEGORIES_QUERY,
} from '../graphql/queries';
import { packageLabelsFor, OfferingPackagesView } from './OfferingPackages';
import { formatPriceRange } from './OfferingDetails';

const PENDING_TEXT = {
  sent_for_review: 'awaiting the vendor’s review', user_requested_changes: 'vendor requested changes',
  user_agreed: 'awaiting signatures', draft: 'draft',
};

// Admin service pages (Caterers, Venues, ...): the registered vendors who have signed a
// contract for this service, with their offerings and packages (e.g. a caterer's menus).
export default function ContractedVendorsSection({ category }) {
  const navigate = useNavigate();
  const { data: accData, loading } = useQuery(VENDOR_ACCOUNTS_QUERY, { fetchPolicy: 'cache-and-network' });
  const { data: offData } = useQuery(VENDOR_OFFERINGS_QUERY, {
    variables: { category, includeUnpublished: true }, fetchPolicy: 'cache-and-network',
  });
  const { data: defsData } = useQuery(VENDOR_OFFERING_FIELD_DEFS_QUERY);
  const { data: catData } = useQuery(ALL_SERVICE_CATEGORIES_QUERY, { variables: { activeOnly: false } });

  const label = (catData?.allServiceCategories || []).find(c => c.name === category)?.displayName || category;
  const labels = packageLabelsFor(defsData?.vendorOfferingFields, category);
  const accounts = accData?.vendorAccounts || [];
  const withService = accounts
    .map(v => ({ ...v, service: v.services.find(s => s.category === category) }))
    .filter(v => v.service);
  const signed = withService.filter(v => v.service.live);
  const pending = withService.filter(v => !v.service.live);
  const offerings = offData?.vendorOfferings || [];

  return (
    <Accordion defaultExpanded disableGutters variant="outlined" sx={{ mb: 3, borderRadius: 2, '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <StorefrontIcon color="primary" />
          <Typography fontWeight={700}>Contracted vendors — {label}</Typography>
          <Chip size="small" color="success" label={`${signed.length} signed`} />
          {pending.length > 0 && <Chip size="small" color="warning" variant="outlined" label={`${pending.length} awaiting contract`} />}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {loading && !accData && <LinearProgress sx={{ mb: 2 }} />}
        {accData && signed.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No registered vendor has a signed contract for {label} yet. Vendors appear here once they and an admin have signed it
            (Admin &gt; Vendors or Admin &gt; Contracts).
          </Alert>
        )}
        <Grid container spacing={2}>
          {signed.map(v => {
            const mine = offerings.filter(o => o.vendorUserId === v.id);
            const contact = mine[0];
            return (
              <Grid item xs={12} lg={6} key={v.id}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h6" fontWeight={700}>{v.businessName}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {v.email}{contact?.contactPhone ? ` · ${contact.contactName ? `${contact.contactName}, ` : ''}${contact.contactPhone}` : ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Contract {v.service.contractRef} · signed · {v.openBookings} open booking(s)
                          {!v.isActive && ' · account deactivated'}
                        </Typography>
                      </Box>
                      <Button size="small" variant="outlined" onClick={() => navigate(`/dashboard/admin/vendors/${v.id}`)}>
                        Manage services
                      </Button>
                    </Stack>
                    <Divider sx={{ my: 1.5 }} />
                    {mine.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        No {label} offering yet — families can't see this vendor for {label} until one is added.
                      </Typography>
                    )}
                    {mine.map(o => (
                      <Box key={o.id} mb={1.5}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography fontWeight={600}>{o.title}</Typography>
                          <Chip size="small" color={o.isPublished ? 'success' : 'default'} variant={o.isPublished ? 'filled' : 'outlined'}
                            label={o.isPublished ? 'Live' : 'Hidden'} />
                        </Stack>
                        <Typography variant="body2" color="primary.main" fontWeight={600}>{formatPriceRange(o)}</Typography>
                        {!o.isPublished && <Typography variant="caption" color="text.secondary">{o.visibilityNote}</Typography>}
                        <OfferingPackagesView labels={labels} packages={o.packages} dense />
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
        {pending.length > 0 && (
          <Box mt={2}>
            <Typography variant="caption" fontWeight={700} color="text.secondary">AWAITING A SIGNED CONTRACT</Typography>
            <Stack direction="row" spacing={1} mt={0.5} flexWrap="wrap" useFlexGap>
              {pending.map(v => (
                <Chip key={v.id} variant="outlined" color="warning" onClick={() => navigate(`/dashboard/admin/vendors/${v.id}`)}
                  label={`${v.businessName} — ${v.service.contractRef || ''} ${PENDING_TEXT[v.service.contractStatus] || v.service.contractStatus}`} />
              ))}
            </Stack>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
